import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate, fmtDateTime } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { BackToDashboard } from "../components/BackToDashboard";
import { CalendarDays, List, ChevronLeft, ChevronRight } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

export default function Inspections() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("list");
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/inspections")
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(e => console.error("Inspections load failed:", e))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-4 sm:px-8 py-8 max-w-7xl mx-auto" data-testid="inspections-page">
      <BackToDashboard />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
        <div>
          <span className="label-eyebrow">Field schedule</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Inspections</h1>
        </div>
        <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden" data-testid="view-toggle">
          <button onClick={() => setView("list")} data-testid="view-list"
            className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 ${view === "list" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}>
            <List size={14} /> List
          </button>
          <button onClick={() => setView("calendar")} data-testid="view-calendar"
            className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 ${view === "calendar" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}>
            <CalendarDays size={14} /> Calendar
          </button>
        </div>
      </div>

      {view === "list" ? (
        <div className="border border-zinc-200 bg-white rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                {["Date & time", "Job", "Address", "Type", "Permit #", "Status"].map(h =>
                  <th key={h} className="px-4 py-2.5 text-left label-eyebrow font-semibold">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  {[...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="px-4 py-3"><Skeleton className="h-4 w-28 font-mono" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-20 font-mono" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-5 w-24 rounded-full" /></td>
                    </tr>
                  ))}
                </>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500" data-testid="inspections-empty">No inspections scheduled.</td></tr>
              ) : items.map(i => (
                <tr key={i.inspection_id} className="row-hover border-b border-zinc-100" data-testid={`inspection-${i.inspection_id}`}>
                  <td className="px-4 py-3 font-mono">{fmtDateTime(i.scheduled_at)}</td>
                  <td className="px-4 py-3">{i.job_nickname}</td>
                  <td className="px-4 py-3 text-zinc-600">{i.address}</td>
                  <td className="px-4 py-3">{i.inspection_type}</td>
                  <td className="px-4 py-3 font-mono text-zinc-600">{i.permit_number}</td>
                  <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <CalendarView month={month} setMonth={setMonth} items={items} />
      )}
    </div>
  );
}

function CalendarView({ month, setMonth, items }) {
  const grid = useMemo(() => {
    const first = new Date(month);
    const startDay = first.getDay();
    const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  const itemsByDay = useMemo(() => {
    const m = {};
    items.forEach(i => {
      const d = new Date(i.scheduled_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (m[key] = m[key] || []).push(i);
    });
    return m;
  }, [items]);

  const monthLabel = month.toLocaleString("en-US", { month: "long", year: "numeric" });
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="calendar-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
        <button onClick={() => shift(-1)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid="cal-prev"><ChevronLeft size={16} /></button>
        <div className="font-display font-bold tracking-tight">{monthLabel}</div>
        <button onClick={() => shift(1)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid="cal-next"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 border-b border-zinc-200">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} className="px-2 py-2 label-eyebrow border-r border-zinc-100 last:border-r-0 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {grid.map((d, i) => {
          const key = d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : null;
          const dayItems = key ? (itemsByDay[key] || []) : [];
          const isToday = d && new Date().toDateString() === d.toDateString();
          return (
            <div key={i} className="min-h-[90px] border-r border-b border-zinc-100 p-1.5 last-in-row:border-r-0">
              {d && (
                <>
                  <div className={`text-xs font-mono ${isToday ? "text-amber-600 font-bold" : "text-zinc-500"}`}>{d.getDate()}</div>
                  <div className="space-y-1 mt-1">
                    {dayItems.slice(0, 2).map(it => (
                      <div key={it.inspection_id} className="text-[10px] truncate px-1 py-0.5 bg-blue-50 text-blue-900 rounded-sm border border-blue-200" title={`${it.inspection_type} – ${it.job_nickname}`}>
                        {it.inspection_type}
                      </div>
                    ))}
                    {dayItems.length > 2 && <div className="text-[10px] text-zinc-500">+{dayItems.length - 2} more</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
